import React, { useState, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import {
  SkipPrevious,
  SkipNext,
  FastRewind,
  FastForward,
  PlayArrow,
  Pause,
  PanTool,
  Directions,
  Schedule,
  Speed,
} from "@mui/icons-material";
import Draggable from "react-draggable";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format, differenceInSeconds, parseISO } from "date-fns";
import { getDistance } from "ol/sphere";
import "./Player.scss";
import { useSwipeable } from "react-swipeable";
import { clamp } from "lodash";
import { Activity } from "lucide-react";
import TargetIcon from "@mui/icons-material/MyLocation";
import { motion, AnimatePresence } from "framer-motion";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
const MOBILE_BREAKPOINT = 768;
const Player = ({
  onMounted,
  activeGroupIndex,
  speedsByGroup,
  pathGroups,
  onPlayPause,
  onPathGroupsChange,
  onSpeedChange,
  onPanToggle,
  playTimeMultiplier,
  isPanning,
  currentGroupSpeeds,
  animationState,
  onStateChange,
  onGroupChange,
  index,
  setIndex,
  setToggleState,
  toggleState,
  activityCount,
  searchFiberHouses,
  loadingActivities,
  setLoadingActivities,
  isAllPaths,
  setIsAllPaths,
}) => {
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= MOBILE_BREAKPOINT
  );
  const [showDetails, setShowDetails] = useState(false);
  const [metrics, setMetrics] = useState({
    distance: "0 km",
    duration: "0m 0s",
    avgSpeed: "0 km/h",
    maxSpeed: "0 km/h",
  });

  // Add expanded state
  const [isExpanded, setIsExpanded] = useState(false);

  const [repSpeeds, setRepSpeeds] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    // Inform the parent that the player has mounted
    onMounted(true);
  }, []);

  // New handler for show more/less
  const handleShowMoreButton = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    setToggleState((prev) => ({
      ...prev,
      activity: isExpanded,
    }));
  }, [isExpanded]);

  useEffect(() => {
    // setIsExpanded(false);
  }, [activeGroupIndex.current]);
  // Toggle details on mobile
  const handleDetailsToggle = () => {
    setShowDetails((prev) => !prev);
  };

  // Handle play/pause
  const handlePlayPause = () => {
    onStateChange((prev) => ({
      ...prev,
      isPlaying: !prev?.isPlaying,
    }));
    // Propagate to parent
    onPlayPause();
  };

  // Handle group navigation
  const handleGroupNavigation = (index) => {
    onGroupChange(index);
  };

  const [isPlaying, setIsPlaying] = useState(false);
  const [speedIndex, setSpeedIndex] = useState(1);
  const speedText = ["x1", "x2", "x4", "x6", "x8", "x16", "x32", "x64"];
  const [scrollPosition, setScrollPosition] = useState(0);
  const [modifiedPathGroups, setModifiedPathGroups] = useState([...pathGroups]);

  // Use passed currentGroupSpeeds directly
  const currentGroupSpeedsNew = currentGroupSpeeds;

  const sectionsRef = useRef(null);

  const handleGroupIndexClick = (index) => {
    activeGroupIndex.current = index;
  };

  const swipeHandlers = useSwipeable({
    onSwiped: (eventData) => {
      if (sectionsRef.current) {
        const { deltaX } = eventData;
        const containerWidth = sectionsRef.current.offsetWidth;
        const contentWidth = sectionsRef.current.scrollWidth;
        const maxScroll = contentWidth - containerWidth;

        setScrollPosition((prevScroll) => {
          const newScroll = prevScroll - deltaX;
          return Math.max(0, Math.min(newScroll, maxScroll));
        });
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  // Calculate metrics when group changes
  useEffect(() => {
    if (!modifiedPathGroups || activeGroupIndex.current === undefined) return;

    const originalGroup = modifiedPathGroups[activeGroupIndex.current];
    if (!originalGroup || originalGroup.length < 2) return;

    // Create filtered group excluding spurious jumps
    const MAX_REASONABLE_SPEED = 200;
    const filteredGroup = [originalGroup[0]];

    for (let i = 1; i < originalGroup.length; i++) {
      const from = filteredGroup[filteredGroup.length - 1];
      const to = originalGroup[i];

      const dist = getDistance([from.lat, from.lon], [to.lat, to.lon]);
      const timeDiff = differenceInSeconds(
        parseISO(to.timestamp),
        parseISO(from.timestamp)
      );
      const speed = timeDiff > 0 ? (dist / timeDiff) * 3.6 : 0;

      if (speed <= MAX_REASONABLE_SPEED) {
        filteredGroup.push(to);
      } else {
        console.warn({
          speed,
          to,
        });
      }
    }

    // Update the pathGroups array with the filtered group
    const updatedPathGroups = [...modifiedPathGroups];
    updatedPathGroups[activeGroupIndex.current] = filteredGroup;
    setModifiedPathGroups(updatedPathGroups);

    // Notify parent of filtered groups
    if (onPathGroupsChange) {
      onPathGroupsChange(updatedPathGroups); // <-- Here
    }

    if (filteredGroup.length < 2) return;

    const calculateMetrics = () => {
      try {
        const startTime = parseISO(filteredGroup[0].timestamp);
        const endTime = parseISO(
          filteredGroup[filteredGroup.length - 1].timestamp
        );
        const durationSec = differenceInSeconds(endTime, startTime);

        let totalDistance = 0;
        let maxSpeed = 0;
        const speeds = [];

        for (let i = 1; i < filteredGroup.length; i++) {
          const from = filteredGroup[i - 1];
          const to = filteredGroup[i];
          const dist = getDistance([from.lat, from.lon], [to.lat, to.lon]);
          const timeDiff = differenceInSeconds(
            parseISO(to.timestamp),
            parseISO(from.timestamp)
          );
          const speed = timeDiff > 0 ? (dist / timeDiff) * 3.6 : 0;

          totalDistance += dist;
          speeds.push(speed);
          if (speed > maxSpeed) maxSpeed = speed;
        }

        setRepSpeeds(speeds.map((speed, index) => ({ index, speed })) || []);

        setMetrics({
          distance: `${(totalDistance / 1000).toFixed(1)} km`,
          duration: formatDuration(durationSec),
          avgSpeed: `${(totalDistance / 1000 / (durationSec / 3600)).toFixed(
            1
          )} km/h`,
          maxSpeed: `${maxSpeed.toFixed(1)} km/h`,
        });
      } catch (error) {
        console.error("Error calculating metrics:", error);
      }
    };

    calculateMetrics();
  }, [activeGroupIndex.current, pathGroups]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getSpeedData = () => {
    if (!speedsByGroup || activeGroupIndex.current === undefined) return [];
    // return speedsByGroup[activeGroupIndex.current]?.map((speed, index) => ({
    //   index,
    //   speed: speed * 3.6 // Convert m/s to km/h
    // })) || [];
    return repSpeeds;
  };

  return (
    <Draggable bounds="parent" handle=".player-handle">
      <div className="player">
        <div className="player-handle">
          <div className="handle-dots"></div>
        </div>
        <div className="player-content">
          {/* Mobile-only Show Details Toggle */}
          {isMobile && (
            <motion.button
              onClick={handleDetailsToggle}
              className="details-toggle-btn"
              style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #ccc",
                borderRadius: 4,
                padding: "4px 8px",
                background: "transparent",
                cursor: "pointer",
                overflow: "hidden",
              }}
              initial={false}
              animate={{
                width: 140,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <span style={{ flexGrow: 1, fontSize: 12 }}>
                {showDetails ? "Hide Details" : "Show Details"}
              </span>
              <motion.div
                initial={false}
                animate={{ rotate: showDetails ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ExpandMoreIcon fontSize="small" />
              </motion.div>
            </motion.button>
          )}
          <AnimatePresence initial={false}>
            {/* Details Section: metrics-grid and speed-chart */}
            {(!isMobile || showDetails) && (
              <Collapse
                in={isMobile ? showDetails : true}
                timeout={300}
                unmountOnExit={isMobile}
                style={{ width: "100%" }}
              >
                <div className="metrics-grid">
                  <div className="metric-card">
                    <Directions fontSize="small" />
                    <div>
                      <span className="metric-label">Distance</span>
                      <span className="metric-value">{metrics.distance}</span>
                    </div>
                  </div>

                  <div className="metric-card">
                    <Schedule fontSize="small" />
                    <div>
                      <span className="metric-label">Duration</span>
                      <span className="metric-value">{metrics.duration}</span>
                    </div>
                  </div>

                  <div className="metric-card">
                    <Speed fontSize="small" />
                    <div>
                      <span className="metric-label">Avg Speed</span>
                      <span className="metric-value">{metrics.avgSpeed}</span>
                    </div>
                  </div>

                  <div className="metric-card">
                    <Speed fontSize="small" />
                    <div>
                      <span className="metric-label">Max Speed</span>
                      <span className="metric-value">{metrics.maxSpeed}</span>
                    </div>
                  </div>
                </div>
              </Collapse>
            )}
          </AnimatePresence>

          <div className="controls-row">
            <Button
              id="showMoreBtn"
              variant="text"
              onClick={handleShowMoreButton}
              className="show-more-btn"
            >
              {!isExpanded
                ? `Show Activities`
                : loadingActivities
                ? "Loading..."
                : `Hide Activities (${activityCount})`}
            </Button>
          </div>
          {(!isMobile || showDetails) && (
            <div className="speed-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={getSpeedData()}
                  margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                >
                  <defs>
                    <linearGradient
                      id="speedGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#4CAF50"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>

                  <XAxis
                    dataKey="index"
                    tick={{ fontSize: 10 }}
                    label={{
                      value: "Time Points",
                      position: "bottom",
                      fontSize: 10,
                    }}
                  />

                  <YAxis
                    unit="km/h"
                    tick={{ fontSize: 10 }}
                    label={{
                      value: "Speed",
                      angle: -90,
                      position: "left",
                      fontSize: 10,
                    }}
                  />

                  <Tooltip
                    formatter={(value) => [`${value.toFixed(1)} km/h`, "Speed"]}
                    labelFormatter={(index) => `Point ${index + 1}`}
                    contentStyle={{
                      padding: "4px 8px",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "0.65rem", // Match your metric labels
                    }}
                    itemStyle={{
                      padding: 0,
                      fontSize: "0.7rem", // Slightly larger than label
                    }}
                    labelStyle={{
                      display: "none", // Hide label if you want minimal design
                    }}
                  />

                  <Area
                    type="monotone"
                    dataKey="speed"
                    stroke="#4CAF50"
                    fill="url(#speedGradient)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="seeker-container">
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              className="seeker"
              id="seeker"
            />
          </div>
          <div className="time-display-container">
            <span className="time-display"></span>
          </div>
          <div className="controls-row">
            <Button
              disabled={isAllPaths}
              id="previousBtn"
              className="control-btn"
              variant="text"
              onClick={() =>
                handleGroupNavigation(activeGroupIndex.current - 1)
              }
            >
              <SkipPrevious fontSize="small" />
            </Button>
            <Button
              id="rewindBtn"
              className="control-btn"
              variant="text"
              onClick={() => {}}
            >
              <FastRewind fontSize="small" />
            </Button>
            <Button
              id="PlayPauseBtn"
              className="control-btn"
              variant="text"
              onClick={onPlayPause}
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </Button>
            <Button
              id="forwardBtn"
              className="control-btn"
              variant="text"
              onClick={() => {}}
            >
              <FastForward fontSize="small" />
            </Button>
            <Button
              disabled={isAllPaths}
              id="nextBtn"
              className="control-btn"
              variant="text"
              onClick={() =>
                handleGroupNavigation(activeGroupIndex.current + 1)
              }
            >
              <SkipNext fontSize="small" />
            </Button>
          </div>
          <div className="controls-row">
            <Button
              id="speedBtn"
              variant="outlined"
              onClick={onSpeedChange}
              className="speed-btn"
            >
              {speedText[playTimeMultiplier - 1]}
            </Button>
            <Button
              id="panSwitch"
              variant="text"
              onClick={onPanToggle}
              className={`target-btn ${isPanning ? "active" : ""}`}
              aria-label="Toggle target"
            >
              {/* <PanTool fontSize="small" /> */}
              <TargetIcon fontSize="small" />
            </Button>
          </div>
          <div className="controls-row">
            <Button
              id="allPathsBtn"
              variant={isAllPaths ? "contained" : "outlined"}
              onClick={() => {
                const newMode = !isAllPaths;
                setIsAllPaths(newMode);

                if (newMode) {
                  // Reset to first group if at end
                  if (activeGroupIndex.current >= pathGroups.length - 1) {
                    handleGroupNavigation(0);
                  } else {
                    handleGroupNavigation(activeGroupIndex.current);
                  }
                }
              }}
              className={`all-paths-btn ${isAllPaths ? "active" : ""}`}
            >
              {isAllPaths ? "Stop All Paths" : "Play All Paths"}
            </Button>
          </div>
          <div
            ref={sectionsRef}
            {...swipeHandlers}
            className="sections-container"
            disabled={isAllPaths}
          >
            {pathGroups.map((group, index) => (
              <Button
                disabled={isAllPaths}
                key={index}
                id={`groupBtn-${index}`}
                className={`section-btn ${
                  activeGroupIndex.current === index ? "active" : ""
                }`}
                onClick={() => handleGroupNavigation(index)}
                variant="text"
                size="small"
              >
                {index + 1}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Draggable>
  );
};

export default Player;
